import {
  BitkitUISideNav,
  BitkitUISideNavItemProps,
  IconBuildCache,
  IconBuildCacheFilled,
  IconCi,
  IconCiFilled,
  IconCpu,
  IconDashboard,
  IconDashboardFilled,
  IconDeployment,
  IconInsights,
  IconInsightsFilled,
  IconPlusCircle,
  IconQuestionCircle,
  IconRelease,
  IconReleaseFilled,
  IconSettings,
  IconSettingsFilled,
  IconTerminal,
  useResponsive,
} from '@bitrise/bitkit-v2';
import { useContext } from 'react';

import McpNavWidget from '@/components/ConnectMcp/McpNavWidget';
import { HEADER_HEIGHT } from '@/constants';
import URLs from '@/enums/urls';
import { GlobalProps } from '@/models/GlobalProps';
import { releaseManagementConnectedAppsPagePath } from '@/utils/monolithApiRouteService';
import { isCiProductArea } from '@/utils/productArea';

import { GlobalPropsContext } from '../../GlobalPropsContext';
import { useSettingsNavHrefOverride } from './settingsNavOverride';
import WorkspaceSelector from './WorkspaceSelector/WorkspaceSelector';

type NavItem = Omit<BitkitUISideNavItemProps, 'children'> & {
  label: string;
  // Rows match the path by prefix. 'full' is for one whose own sub-pages belong to another row —
  // the workspace settings sit under the dashboard's href.
  match?: 'full' | 'prefix';
};

const isCurrentPath = (href: string, match: NavItem['match'] = 'prefix') => {
  const { pathname } = window.location;
  return match === 'full' ? pathname === href : pathname.startsWith(href);
};

type UISideNavProps = {
  isOpen: boolean;
  marginBlockStart: string;
  onOpenChange: (isOpen: boolean) => void;
};
const UISideNav = ({ isOpen, marginBlockStart, onOpenChange }: UISideNavProps) => {
  const {
    account,
    user,
    featureFlags: { account: accountFeatureFlags },
  } = useContext(GlobalPropsContext) as GlobalProps;

  const { isMobile } = useResponsive();
  const settingsHrefOverride = useSettingsNavHrefOverride();

  // The mobile drawer places itself; below the tablet breakpoint these props would fight it. The
  // rail's own `height: 100%` would measure the whole viewport from the header down, so the insets
  // have to drive its height instead — the Intercom banner shifts the top edge at any time.
  const railPlacement = isMobile
    ? undefined
    : ({
        height: 'auto',
        insetBlockEnd: 0,
        insetBlockStart: HEADER_HEIGHT,
        insetInlineStart: 0,
        marginBlockStart,
        position: 'fixed',
        zIndex: 300,
      } as const);

  const navItems: NavItem[] = account
    ? [
        {
          href: `/workspaces/${account.slug}`,
          icon: IconDashboard,
          label: 'Dashboard',
          match: 'full',
          selectedIcon: IconDashboardFilled,
        },
        {
          // The CI product area spans several roots, so its own check stands in for the href match.
          href: '/ci',
          icon: IconCi,
          label: 'Bitrise CI',
          selected: isCiProductArea(),
          selectedIcon: IconCiFilled,
        },
        { href: '/build-cache', icon: IconBuildCache, label: 'Build Cache', selectedIcon: IconBuildCacheFilled },
        {
          href: releaseManagementConnectedAppsPagePath(account.slug),
          icon: IconRelease,
          label: 'Releases',
          selectedIcon: IconReleaseFilled,
        },
        ...(accountFeatureFlags?.buildInsightsEnabled
          ? [{ href: '/insights', icon: IconInsights, label: 'Insights', selectedIcon: IconInsightsFilled }]
          : []),
        ...((accountFeatureFlags?.enableRemoteDevEnv || accountFeatureFlags?.rdeTrialAvailable) && account.isRdeAdmin
          ? [{ href: `/dev-environments/${account.slug}`, icon: IconTerminal, label: 'Dev Environments' }]
          : []),
        ...(account.showBuildHub ? [{ href: '/build-hub', icon: IconCpu, label: 'Build Hub' }] : []),
        ...(account.isMember
          ? [
              {
                className: 'platform-nav-settings',
                href: settingsHrefOverride ?? `/workspaces/${account.slug}/settings`,
                icon: IconSettings,
                label: 'Settings',
                selectedIcon: IconSettingsFilled,
              },
            ]
          : []),
      ]
    : [{ href: '/create-workspace', icon: IconPlusCircle, label: 'Create workspace' }];

  const mcpWidget =
    user && accountFeatureFlags?.mcpSignpost ? <McpNavWidget status={user.mcpConnectionStatus} /> : null;

  const helpLinks = isMobile ? (
    <>
      {account && (
        <BitkitUISideNav.Link href="/getting-started" icon={IconDeployment}>
          Getting Started
        </BitkitUISideNav.Link>
      )}
      <BitkitUISideNav.Link href={URLs.SUPPORT_CENTER} icon={IconQuestionCircle}>
        Support Center
      </BitkitUISideNav.Link>
    </>
  ) : null;

  return (
    <BitkitUISideNav className="platform-nav" onOpenChange={onOpenChange} open={isOpen} {...railPlacement}>
      {account && <WorkspaceSelector />}
      <BitkitUISideNav.Body>
        {navItems.map(({ href, label, match, selected, ...item }) => (
          <BitkitUISideNav.Item href={href} key={href} selected={selected ?? isCurrentPath(href, match)} {...item}>
            {label}
          </BitkitUISideNav.Item>
        ))}
      </BitkitUISideNav.Body>
      {(mcpWidget || helpLinks) && (
        <BitkitUISideNav.Footer label={!mcpWidget && helpLinks ? 'Help' : undefined}>
          {mcpWidget}
          {mcpWidget && helpLinks && <BitkitUISideNav.FooterHeading>Help</BitkitUISideNav.FooterHeading>}
          {helpLinks}
        </BitkitUISideNav.Footer>
      )}
    </BitkitUISideNav>
  );
};

export default UISideNav;
